import { IUserInfo } from "../DataStorage/IUserInfo";
import { UserData } from "./UserData";
import { DataStoreQueryCondition, DataStoreQueryConditionType } from "../DataStorage/DataStoreQueryCondition";
import { IUserDataStorage } from "./IUserDataStorage";
import { AppGlueCorePlugins } from "../../AppGlueCorePlugins";
import { AppGlueError } from "../../AppGlueError";
import { BuilderTypes } from "../BuilderStorage/BuilderTypes";
import { IDataStore } from "../DataStorage/IDataStore";
import { DataDefinition } from "../../Common/Data/DataDefinition";
import { BuilderStorage } from "../BuilderStorage/BuilderStorage";

//import {BuilderStorageUtilities} from "../BuilderStorage/BuilderStorageUtilities";

export class UserDataStorage implements IUserDataStorage {
    getDataStore(user: IUserInfo): IDataStore<UserData> {
        if (!AppGlueCorePlugins.DataStoreFactory) {
            throw new AppGlueError("DataStore plugin not initialized");
        }

        return AppGlueCorePlugins.DataStoreFactory!.getDataStoreByName<UserData>("UserData_" + user.teamName)!;
    }

    async create(
        user: IUserInfo,
        userDataId: string,
        dataDefinitionId: string,
        data: object,
        listName?: string,
    ): Promise<UserData> {
        let dd = await AppGlueCorePlugins.BuilderStorage?.getBuilderData(
            user,
            BuilderTypes.DataDefinition,
            dataDefinitionId,
        );

        if (!dd) throw new AppGlueError("Data defintion not found");

        let ud = new UserData(userDataId, (await dd).name, dataDefinitionId, data, listName);
        return this.store(user, ud);
    }

    async delete(user: IUserInfo, dataDefinitionId: string, userDataId: string, listName?: string): Promise<boolean> {
        let item = await this.get(user, dataDefinitionId, userDataId, listName);
        let ds = this.getDataStore(user);
        return ds.delete(user, item.id!);
    }

    async get(user: IUserInfo, dataDefinitionId: string, userDataId: string, listName?: string): Promise<UserData> {
        let ds = this.getDataStore(user);

        let searchFields: DataStoreQueryCondition[] = [];

        let def = await this.getDataDefinitionForDataType(user, dataDefinitionId);

        searchFields.push(
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "dataDefinitionId", dataDefinitionId),
        );
        searchFields.push(new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "userDataId", userDataId));
        searchFields.push(
            new DataStoreQueryCondition(
                DataStoreQueryConditionType.Equals,
                "listName",
                listName ?? def.builderStorage.name,
            ),
        );

        return ds.queryForSingle(user, searchFields);
    }

    async listAll(user: IUserInfo, listName?: string): Promise<UserData[]> {
        let ds = this.getDataStore(user);
        let searchFields: DataStoreQueryCondition[] = [];

        if (listName)
            searchFields.push(new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "listName", listName));

        return ds.query(user, searchFields);
    }

    async listAllByType(user: IUserInfo, dataDefinitionId: string, listName?: string): Promise<UserData[]> {
        let ds = this.getDataStore(user);
        let def = await this.getDataDefinitionForDataType(user, dataDefinitionId);

        let searchFields: DataStoreQueryCondition[] = [];
        searchFields.push(
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "dataDefinitionId", dataDefinitionId),
        );
        searchFields.push(
            new DataStoreQueryCondition(
                DataStoreQueryConditionType.Equals,
                "listName",
                listName ?? def.builderStorage.name,
            ),
        );

        return ds.query(user, searchFields);
    }

    async listByCriteria(
        user: IUserInfo,
        dataDefinitionId: string,
        searchFields: DataStoreQueryCondition | DataStoreQueryCondition[],
        listName?: string,
    ): Promise<UserData[]> {
        let ds = this.getDataStore(user);

        if (!Array.isArray(searchFields)) searchFields = [searchFields];

        for (let f of searchFields) {
            f.fieldName = "search_" + f.fieldName;
        }

        searchFields.push(
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "dataDefinitionId", dataDefinitionId),
        );

        let def = await this.getDataDefinitionForDataType(user, dataDefinitionId);

        searchFields.push(
            new DataStoreQueryCondition(
                DataStoreQueryConditionType.Equals,
                "listName",
                listName ?? def.builderStorage.name,
            ),
        );

        return ds.query(user, searchFields);
    }

    async store(user: IUserInfo, data: UserData): Promise<UserData> {
        let ds = this.getDataStore(user);
        let def = await this.getDataDefinitionForDataType(user, data.dataDefinitionId);

        let name = "";

        for (let f of def.dataDefinition.fields) {
            let metaData = def.dataDefinition.getFieldMetaData(f.name);

            // put search fields on object
            if (metaData.searchField) {
                let v = Reflect.get(data.data, f.name);
                Reflect.set(data, "search_" + f.name, v);
            }

            if (metaData.includeInName) {
                let v = Reflect.get(data.data, f.name);
                if (name === "") name = v;
                else name = name + " " + v;
            }
        }

        // set name field
        data.name = name;

        return ds.store(user, data);
    }

    private async getDataDefinitionForDataType(
        user: IUserInfo,
        dataDefinitionId: string,
    ): Promise<{
        dataDefinition: DataDefinition;
        builderStorage: BuilderStorage;
    }> {
        let dd = await AppGlueCorePlugins.BuilderStorage?.getBuilderData(
            user,
            BuilderTypes.DataDefinition,
            dataDefinitionId,
        );

        if (!dd) throw new AppGlueError("Data defintion not found");

        let def = new DataDefinition();
        def.setStorageData(dd.builderData!);
        return { dataDefinition: def, builderStorage: dd };
    }
}
